import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { useState, useEffect } from 'react';

interface AccessControlResult {
  canAccess: boolean;
  requiresVerification: boolean;
  isVerified: boolean;
  userRole: UserRole | null;
  isLoading: boolean;
}

export const useAccessControl = (): AccessControlResult => {
  const { user } = useAuth();
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const client = generateClient<Schema>();

  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!user || (user.role !== UserRole.THERAPIST && user.role !== UserRole.CLINICIAN && user.role !== UserRole.INTERNS)) {
        setIsVerified(true); // Non-providers are considered "verified"
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has a verified provider verification
        const verifications = await client.models.ProviderVerification.list({
          filter: {
            userId: { eq: user.id },
            verified: { eq: true }
          },
          authMode: 'apiKey'
        });

        setIsVerified(verifications.data && verifications.data.length > 0);
      } catch (error) {
        console.error('Error checking verification status:', error);
        setIsVerified(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkVerificationStatus();
  }, [user, client]);

  if (!user) {
    return {
      canAccess: false,
      requiresVerification: false,
      isVerified: false,
      userRole: null,
      isLoading: false,
    };
  }

  const requiresVerification = user.role === UserRole.THERAPIST || user.role === UserRole.CLINICIAN || user.role === UserRole.INTERNS;
  const canAccess = !requiresVerification || isVerified;

  return {
    canAccess,
    requiresVerification,
    isVerified,
    userRole: user.role,
    isLoading,
  };
};

export const useProviderAccess = (): {
  canAccessProviderFeatures: boolean;
  showPendingVerification: boolean;
  userRole: UserRole | null;
  isLoading: boolean;
} => {
  const { canAccess, requiresVerification, isVerified, userRole, isLoading } = useAccessControl();

  return {
    canAccessProviderFeatures: canAccess,
    showPendingVerification: requiresVerification && !isVerified,
    userRole,
    isLoading,
  };
};
